import { defineStore } from 'pinia';
import { useLocalStorage } from '@vueuse/core';
import OrdersService from '@/api/services/orders.service';
import { IOrder } from '@/interfaces/IOrder';
import { OrderPaymentStatus } from '@/enums/OrderEnums';
import { Globals } from '@/modules/Globals';

interface IPrintedCounters {
  // Map index -> amount printed
  [index: number]: number;
}

interface IPrintedEntry {
  updatedAt: number; // timestamp of last successful print
  printT?: IPrintedCounters;
  printC?: IPrintedCounters;
  printE?: IPrintedCounters;
  /** Epoch ms of the local printed-while-paid stamp (OnPaid ticket dedupe). */
  printedPaidAt?: number;
}

interface IPendingMarkRequest {
  orderId: number;
  indexes: number[];
  printT?: number[];
  printC?: number[];
  printE?: number[];
  printedPaid?: boolean;
  retries: number;
  lastTry: number;
  /** When the request was first queued. Optional: older persisted entries lack it. */
  queuedAt?: number;
}

const BACKOFF_BASE_MS = 2_000; // 2s,4s,8s... capped at ~8.5min
const PENDING_MAX_AGE_MS = 48 * 60 * 60 * 1000; // keep retrying while < 48h old
const FLUSH_INTERVAL_MS = 60 * 1000;
const VACUUM_INTERVAL_MS = 60 * 60 * 1000;
const TTL_DAYS = 7;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;
const MAX_ENTRIES = 50000; // failsafe upper bound

/**
 * 4xx responses that will never succeed on retry (permissions, validation).
 * Excludes 408 (timeout) and 429 (rate limit), which are transient.
 */
function _isSemanticClientError(e: unknown): boolean {
  const status = (e as { response?: { status?: number } })?.response?.status;
  return (
    typeof status === 'number' &&
    status >= 400 &&
    status < 500 &&
    status !== 408 &&
    status !== 429
  );
}

function dLog(...args: any[]) {
  if (Globals.IsLocalhost) {
    console.log('[PrintedStore]', ...args);
  }
}

export const usePrintedStore = defineStore('printed', () => {
  /** Device-local record of how many units of each order-item were printed on THIS workstation */
  const printed = useLocalStorage<Record<number, IPrintedEntry>>( // orderId -> entry
    'printed-cache',
    {}
  );

  /** Requests that failed to reach the backend. Will be retried with exponential backoff */
  const pending = useLocalStorage<IPendingMarkRequest[]>('printed-pending', []);

  /* ------------------------------------------------------------------ */
  /* Helpers                                                            */
  /* ------------------------------------------------------------------ */

  function _mergeArrayIntoMap(
    src: number[] | undefined,
    dst: IPrintedCounters,
    indexes: number[]
  ) {
    if (!src) return;
    src.forEach((amount, i) => {
      const idx = indexes[i];
      dLog('mergeArrayIntoMap', {
        idx,
        amount,
        dst,
        dstIdx: dst[idx],
      });
      dst[idx] = Math.max(dst[idx] ?? 0, amount);
    });
  }

  /** Called after a SUCCESSFUL print */
  function addPrinted(req: Omit<IPendingMarkRequest, 'retries' | 'lastTry'>) {
    const { orderId, indexes, printT, printC, printE, printedPaid } = req;

    // Marker-only requests (empty indexes) still cache the paid stamp.
    if ((!indexes || !indexes.length) && !printedPaid) return;

    dLog('addPrinted', {
      orderId,
      indexes,
      printT,
      printC,
      printE,
      printedPaid,
    });

    if (!printed.value[orderId])
      printed.value[orderId] = { updatedAt: Date.now() };

    const entry = printed.value[orderId];
    entry.updatedAt = Date.now();

    if (printT) {
      if (!entry.printT) entry.printT = {};
      _mergeArrayIntoMap(printT, entry.printT, indexes);
    }
    if (printC) {
      if (!entry.printC) entry.printC = {};
      _mergeArrayIntoMap(printC, entry.printC, indexes);
    }
    if (printE) {
      if (!entry.printE) entry.printE = {};
      _mergeArrayIntoMap(printE, entry.printE, indexes);
    }

    if (printedPaid) {
      entry.printedPaidAt ??= Date.now();
    }

    // Force Vue to recognise deep change so useLocalStorage writes the update
    printed.value = { ...printed.value };
  }

  /** Sync local counters into a freshly downloaded order to avoid regressions */
  function mergeIntoOrder(order: IOrder) {
    const cached = printed.value[order.id];
    if (!cached) return;

    dLog('mergeIntoOrder', order.id, cached);

    // Counter overlay needs items; the order-level marker below does not.
    if (order.orderItems) {
      const apply = (
        map: IPrintedCounters | undefined,
        accessor: (item: any) => number | undefined,
        setter: (item: any, val: number) => void
      ) => {
        if (!map) return;
        order.orderItems.forEach((item) => {
          const idx = item.index;
          const cachedAmount = map[idx];
          if (cachedAmount === undefined) return;
          setter(item, Math.max(accessor(item) ?? 0, cachedAmount));
        });
      };

      apply(
        cached.printT,
        (i) => i.printT,
        (i, v) => (i.printT = v)
      );
      apply(
        cached.printC,
        (i) => i.printC,
        (i, v) => (i.printC = v)
      );
      apply(
        cached.printE,
        (i) => i.printE,
        (i, v) => (i.printE = v)
      );
    }

    if (cached.printedPaidAt) {
      if (order.paymentStatus === OrderPaymentStatus.Paid) {
        // Overlay: protects the OnPaid gate on this device while the
        // server-side stamp is still in flight (or queued offline).
        if (!order.printedPaidAt) {
          order.printedPaidAt = new Date(cached.printedPaidAt).toISOString();
        }
      } else {
        // Self-heal: the server cleared the marker on un-pay; a stale cached
        // marker would suppress the legitimate reprint after re-pay. Only the
        // marker is purged, counters stay.
        delete cached.printedPaidAt;
        printed.value = { ...printed.value };
      }
    }
  }

  /** Queue a failed markPrinted request for retry */
  function queuePending(
    req: Omit<IPendingMarkRequest, 'retries' | 'lastTry' | 'queuedAt'>
  ) {
    dLog('queuePending', req);
    pending.value.push({
      ...req,
      retries: 0,
      lastTry: 0,
      queuedAt: Date.now(),
    });
  }

  /* ------------------------------------------------------------------ */
  /* Retry + Vacuum                                                     */
  /* ------------------------------------------------------------------ */

  async function flushPending() {
    const now = Date.now();
    for (const entry of [...pending.value]) {
      // Entries persisted by older builds have no queuedAt: adopt one so they
      // age out normally instead of living forever.
      if (!entry.queuedAt) entry.queuedAt = now;

      const wait = BACKOFF_BASE_MS * 2 ** Math.min(entry.retries, 8);
      if (now - entry.lastTry < wait) continue;

      entry.lastTry = now;
      try {
        dLog('flushPending try', entry);
        await OrdersService.v2MarkPrinted(entry);
        dLog('flushPending success', entry);
        // success -> remove
        const idx = pending.value.indexOf(entry);
        if (idx !== -1) pending.value.splice(idx, 1);
      } catch (e) {
        entry.retries += 1;

        const expired = now - entry.queuedAt > PENDING_MAX_AGE_MS;
        if (_isSemanticClientError(e) || expired) {
          console.error(
            `[PrintedStore] dropping pending markPrinted (${
              expired ? 'older than 48h' : 'rejected by server'
            })`,
            entry,
            e
          );
          const idx = pending.value.indexOf(entry);
          if (idx !== -1) pending.value.splice(idx, 1);
        }
        // Otherwise keep retrying with capped backoff while the entry is fresh.
      }
    }
  }

  function vacuum() {
    const now = Date.now();
    let removed = 0;
    for (const [orderId, entry] of Object.entries(printed.value)) {
      if (now - entry.updatedAt > TTL_MS) {
        delete printed.value[Number(orderId)];
        removed++;
      }
    }

    // failsafe upper bound
    const keys = Object.keys(printed.value);
    if (keys.length > MAX_ENTRIES) {
      keys
        .sort(
          (a, b) =>
            printed.value[Number(a)].updatedAt -
            printed.value[Number(b)].updatedAt
        )
        .slice(0, keys.length - MAX_ENTRIES)
        .forEach((k) => delete printed.value[Number(k)]);
    }

    if (removed) dLog('vacuum removed old entries', removed);
  }

  // initial housekeeping and schedule periodic tasks
  vacuum();
  flushPending();
  if (typeof window !== 'undefined') {
    window.addEventListener('online', flushPending);
  }
  setInterval(() => {
    flushPending().catch(console.error);
  }, FLUSH_INTERVAL_MS);
  setInterval(vacuum, VACUUM_INTERVAL_MS);

  return {
    printed,
    pending,
    mergeIntoOrder,
    addPrinted,
    queuePending,
    flushPending,
  };
});
